import { coalesce, Config, getLookupAsync } from "@serenity-is/corelib";
import { LanguageRow } from "../../ServerTypes/Administration";

export class LanguageSelector {
    private static initialized = false;

    static initialize() {
        if (this.initialized) return;
        this.initialized = true;

        // Wait for DOM to be ready
        document.addEventListener('DOMContentLoaded', () => {
            const menu = document.querySelector('.s-language-selection-menu');
            if (!menu) return;

            const currentLanguage = this.getCurrentLanguage();
            this.populateLanguageMenu(menu as HTMLElement, currentLanguage);
        });
    }

    private static getCurrentLanguage(): string {
        // Try to get from cookie first
        let lang = this.getCookie('LanguagePreference');
        if (!lang) {
            // Fallback to document language or default
            lang = document.documentElement.lang || 'en';
        }
        return coalesce(lang, 'en');
    }

    private static getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }

    private static setCookie(name: string, value: string, options: { path?: string; expires?: Date; secure?: boolean; sameSite?: string } = {}) {
        let cookieString = `${name}=${encodeURIComponent(value)}`;
        
        if (options.path) {
            cookieString += `; path=${options.path}`;
        }
        
        if (options.expires) {
            cookieString += `; expires=${options.expires.toUTCString()}`;
        }

        // Security: Always set Secure and SameSite flags
        //cookieString += `; Secure`;
        cookieString += `; SameSite=${options.sameSite || 'Lax'}`;
        
        document.cookie = cookieString;
    }

    private static async populateLanguageMenu(menu: HTMLElement, currentLanguage: string) {
        try {
            const lookup = await getLookupAsync<LanguageRow>('Administration.Language');
            
            // Clear existing items
            menu.innerHTML = '';

            // Validate current language
            let validatedLanguage = currentLanguage;
            if (!lookup.items.some(x => x.LanguageId === currentLanguage)) {
                const idx = currentLanguage.lastIndexOf('-');
                if (idx >= 0) {
                    validatedLanguage = currentLanguage.substr(0, idx);
                    if (!lookup.items.some(x => x.LanguageId === validatedLanguage)) {
                        validatedLanguage = 'en';
                    }
                } else {
                    validatedLanguage = 'en';
                }
            }

            // Create menu items for each language
            for (const lang of lookup.items) {
                const item = document.createElement('li');
                const link = document.createElement('a');
                link.className = 'dropdown-item';
                link.href = 'javascript:void(0)';
                link.textContent = lang.LanguageName || lang.LanguageId || '';
                
                // Mark current language as active
                if (lang.LanguageId === validatedLanguage) {
                    link.classList.add('active');
                }

                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.changeLanguage(lang.LanguageId || '');
                });

                item.appendChild(link);
                menu.appendChild(item);
            }

        } catch (error) {
            console.error('Error loading languages:', error);
            // Create fallback English option
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.className = 'dropdown-item';
            link.href = 'javascript:void(0)';
            link.textContent = 'English';
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.changeLanguage('en');
            });
            item.appendChild(link);
            menu.appendChild(item);
        }
    }

    private static async changeLanguage(languageId: string) {
        // Security: Set language cookie server-side with HTTPOnly flag
        try {
            const response = await fetch('/Services/Common/UserPreference/SetLanguage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ languageId })
            });
            
            if (response.ok) {
                window.location.reload();
            } else {
                console.error('Failed to set language preference');
            }
        } catch (error) {
            console.error('Error setting language:', error);
        }
    }
}

// Auto-initialize when module is loaded
LanguageSelector.initialize();
